// Pure helpers for the admin Results entry screen: turning typed / pasted text
// into match records in the shape used by darts-results.json.

import { STAGES, cleanName, buildNameCanon, canonicaliseMatchNames } from "./rankings";

var STAGE_ALIASES = {
  "rr": "Round Robin", "round robin": "Round Robin", "group": "Round Robin", "groups": "Round Robin",
  "r64": "Round of 64", "last 64": "Round of 64", "round of 64": "Round of 64",
  "r32": "Round of 32", "last 32": "Round of 32", "round of 32": "Round of 32",
  "r16": "Round of 16", "last 16": "Round of 16", "round of 16": "Round of 16",
  // DartConnect names rounds by how many players are left
  "top 64": "Round of 64", "top 32": "Round of 32", "top 16": "Round of 16",
  "top 8": "Quarter Finals", "top 4": "Semi Finals", "top 2": "Final",
  "qf": "Quarter Finals", "quarter final": "Quarter Finals", "quarter finals": "Quarter Finals", "quarters": "Quarter Finals",
  "sf": "Semi Finals", "semi final": "Semi Finals", "semi finals": "Semi Finals", "semis": "Semi Finals",
  "f": "Final", "final": "Final", "finals": "Final",
};

export function normaliseStage(text) {
  var t = cleanName(text).toLowerCase().replace(/[._-]/g, " ").replace(/\s+/g, " ");
  if (!t) return null;
  var exact = STAGES.filter(function(s) { return s.toLowerCase() === t; })[0];
  if (exact) return exact;
  var m = t.match(/^(cons|consolation|c)\s+(.*)$/);
  if (m) {
    var base = STAGE_ALIASES[m[2]];
    if (base && base !== "Round Robin") return "Consolation " + base;
    return null;
  }
  return STAGE_ALIASES[t] || null;
}

// "" = no grade (club night); null = not recognised
export function normaliseGrade(text) {
  var t = cleanName(text).toLowerCase();
  if (t === "" || t === "none" || t === "-" || t === "n/a") return "";
  var m = t.match(/^(grade\s*)?([abc])$/);
  return m ? "Grade " + m[2].toUpperCase() : null;
}

export function numberOrNull(v) {
  if (v === "" || v == null) return null;
  var n = parseFloat(v);
  return isFinite(n) ? n : null;
}

function isNumeric(s) {
  return s !== "" && /^-?\d+(\.\d+)?$/.test(s);
}

// avg1/avg2 belong to player 1 / player 2; stored against winner/loser.
export function buildMatch(grade, stage, group, p1, p2, s1, s2, a1, a2) {
  var p1Won = s1 > s2;
  return {
    grade: grade || null,
    stage: stage,
    group: stage === "Round Robin" && group ? group : null,
    player1: p1,
    player2: p2,
    score1: s1,
    score2: s2,
    winner_avg: p1Won ? a1 : a2,
    loser_avg: p1Won ? a2 : a1,
  };
}

export function matchToPrefill(m) {
  var p1Won = m.score1 > m.score2;
  var a1 = p1Won ? m.winner_avg : m.loser_avg;
  var a2 = p1Won ? m.loser_avg : m.winner_avg;
  return {
    grade: m.grade || "", stage: m.stage, group: m.group || "", p1: m.player1, p2: m.player2,
    s1: String(m.score1), s2: String(m.score2),
    a1: a1 != null ? String(a1) : "", a2: a2 != null ? String(a2) : "",
    nonce: Date.now(),
  };
}

// Splits one pasted line into { grade, stage, p1, p2, nums[] }.
// Tabs / semicolons / pipes give clean columns. Plain commas are also accepted
// even though names are "Last, First": the trailing numbers (2 scores, optionally
// 2 averages) are peeled off the end and the remaining 4 tokens are two names.
function splitLine(line) {
  var delimiter = null;
  if (line.indexOf("\t") >= 0) delimiter = "\t";
  else if (line.indexOf("|") >= 0) delimiter = "|";
  else if (line.indexOf(";") >= 0) delimiter = ";";

  if (delimiter) {
    var cols = line.split(delimiter).map(function(p) { return p.trim(); });
    if (cols.length < 6) return { error: "expected at least 6 columns" };
    return { grade: cols[0], stage: cols[1], p1: cols[2], p2: cols[3], nums: cols.slice(4, 8) };
  }

  var tokens = line.split(",").map(function(p) { return p.trim(); });
  if (tokens.length < 6) return { error: "expected at least 6 values" };
  var rest = tokens.slice(2);
  var count = 0;
  while (count < 4 && count < rest.length && isNumeric(rest[rest.length - 1 - count])) count++;
  if (count !== 2 && count !== 4) return { error: "couldn't find two scores (and optionally two averages) at the end" };
  var names = rest.slice(0, rest.length - count);
  var nums = rest.slice(rest.length - count);
  var p1, p2;
  if (names.length === 4) { p1 = names[0] + ", " + names[1]; p2 = names[2] + ", " + names[3]; }
  else if (names.length === 2) { p1 = names[0]; p2 = names[1]; }
  else return { error: "couldn't tell where the two player names are — use tabs or | between columns" };
  return { grade: tokens[0], stage: tokens[1], p1: p1, p2: p2, nums: nums };
}

// Maps whatever was typed/pasted onto the spelling the site already uses, so
// "wilden, marty" joins the existing "Wilden, Marty" instead of becoming a new
// player. Unknown names just get sensible capitalisation.
export function makeCanon(knownNames) {
  return buildNameCanon(knownNames || []);
}

export function canonicaliseMatches(matches, canon) {
  return canonicaliseMatchNames(matches, canon);
}

// ---- DartConnect copy/paste -------------------------------------------------
//
// A match copied from DartConnect arrives as separate lines, e.g.
//
//   Finals            <- stage heading (applies to the matches that follow)
//   B1                <- board (ignored)
//   76.42             <- player 1 average
//   Murton, Thomas    <- player 1
//   3  /  -  /  0     <- score
//   Fellingham, Adam  <- player 2
//   70.06             <- player 2 average
//
// Headings such as "Grade A", "Semi Finals", "Consolation Quarter Finals" or
// "Group B" are remembered and applied to every match after them.

var STAGE_EXTRA = {
  "quarterfinals": "Quarter Finals", "quarterfinal": "Quarter Finals",
  "semifinals": "Semi Finals", "semifinal": "Semi Finals",
  "last 8": "Quarter Finals", "last 4": "Semi Finals",
};

function stageFromHeading(text) {
  var t = cleanName(text).toLowerCase().replace(/[._-]/g, " ").replace(/\s+/g, " ");
  if (STAGE_EXTRA[t]) return STAGE_EXTRA[t];
  var m = t.match(/^(cons|consolation|c)\s+(.*)$/);
  if (m && STAGE_EXTRA[m[2]]) return "Consolation " + STAGE_EXTRA[m[2]];
  return normaliseStage(text);
}

function gradeFromHeading(text) {
  var t = cleanName(text).toLowerCase();
  var m = t.match(/\bgrade\s*([abc])\b/) || t.match(/^([abc])\s+(?:grade|draw|section|division)\b/) || t.match(/\b(?:division|draw|section)\s+([abc])\b/);
  return m ? "Grade " + m[1].toUpperCase() : null;
}

var DASH = /^[-–—]$/;
var NAME = /^[^\d,][^,]*,\s*[^\d,].*$/;       // "Last, First"

export function looksLikeDartConnect(text) {
  var lines = text.split(/\r?\n/).map(function(l) { return l.trim(); });
  if (text.indexOf("|") >= 0 || text.indexOf("\t") >= 0) return false;
  return lines.some(function(l) { return DASH.test(l) || /^\d+\s*[-–—]\s*\d+$/.test(l); });
}

// defaults: { grade, stage } used when the paste itself doesn't say.
export function parseDartConnect(text, defaults) {
  defaults = defaults || {};
  var lines = text.split(/\r?\n/).map(function(l) { return l.trim(); }).filter(Boolean);

  // 1) classify every line (collapsing "3" "-" "0" into one score token)
  var tokens = [];
  for (var i = 0; i < lines.length; i++) {
    var l = lines[i];
    var oneLine = l.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (oneLine) { tokens.push({ type: "score", s1: parseInt(oneLine[1], 10), s2: parseInt(oneLine[2], 10) }); continue; }
    if (/^\d+$/.test(l) && i + 2 < lines.length && DASH.test(lines[i + 1]) && /^\d+$/.test(lines[i + 2])) {
      tokens.push({ type: "score", s1: parseInt(l, 10), s2: parseInt(lines[i + 2], 10) });
      i += 2;
      continue;
    }
    if (/^(b|board\s*)\d+$/i.test(l)) { tokens.push({ type: "board", text: l }); continue; }
    if (/^\d+\.\d+$/.test(l)) { tokens.push({ type: "avg", value: parseFloat(l) }); continue; }
    if (/^\d+$/.test(l) || DASH.test(l)) { tokens.push({ type: "other", text: l }); continue; }
    if (NAME.test(l) && !stageFromHeading(l) && !gradeFromHeading(l)) { tokens.push({ type: "name", text: cleanName(l) }); continue; }
    tokens.push({ type: "heading", text: l });
  }

  var matches = [];
  var errors = [];
  var notes = [];
  var stage = defaults.stage || null;
  var grade = defaults.grade != null ? defaults.grade : null;
  var group = "";
  var drawMode = null;   // set by a "Consolation" / "Main Draw" section heading
  var ignoredHeadings = [];

  for (var k = 0; k < tokens.length; k++) {
    var t = tokens[k];
    if (t.type === "heading") {
      var g = gradeFromHeading(t.text);
      var grp = t.text.match(/^(?:group|pool)\s+([A-Za-z0-9]+)$/i);
      var st = stageFromHeading(t.text);
      var consSection = /^(consolation|cons|losers?)(\s+(draw|bracket|section))?$/i.test(cleanName(t.text));
      var mainSection = /^(main|winners?)(\s+(draw|bracket|section))?$/i.test(cleanName(t.text));
      if (g) { grade = g; }
      else if (consSection) { drawMode = "Consolation"; }
      else if (mainSection) { drawMode = "Main"; }
      else if (grp) { stage = "Round Robin"; group = grp[1].toUpperCase(); }
      else if (st) {
        // a plain "Semi-Finals" under a Consolation section heading is a consolation round
        if (drawMode === "Consolation" && st !== "Round Robin" && st.indexOf("Consolation") !== 0) st = "Consolation " + st;
        stage = st; group = "";
      }
      else ignoredHeadings.push(t.text);
      continue;
    }
    if (t.type !== "score") continue;

    var before = tokens[k - 1];
    var before2 = tokens[k - 2];
    var after = tokens[k + 1];
    var after2 = tokens[k + 2];
    var p1 = null, a1 = null, p2 = null, a2 = null;

    if (before && before.type === "name") {            // avg, name, score   (DartConnect)
      p1 = before.text;
      if (before2 && before2.type === "avg") a1 = before2.value;
    } else if (before && before.type === "avg" && before2 && before2.type === "name") { // name, avg, score
      p1 = before2.text; a1 = before.value;
    }
    var consumed = 0;
    if (after && after.type === "name") {              // score, name, avg   (DartConnect)
      p2 = after.text; consumed = 1;
      // an average directly followed by a name is the NEXT match's player 1 average
      var after3 = tokens[k + 3];
      if (after2 && after2.type === "avg" && !(after3 && after3.type === "name")) { a2 = after2.value; consumed = 2; }
    } else if (after && after.type === "avg" && after2 && after2.type === "name") {     // score, avg, name
      a2 = after.value; p2 = after2.text; consumed = 2;
    }

    var label = "Match " + (matches.length + errors.length + 1);
    if (!p1 || !p2) { errors.push(label + ": couldn't find both player names around the score " + t.s1 + "-" + t.s2 + "."); continue; }
    if (t.s1 === t.s2) { errors.push(label + " (" + p1 + " v " + p2 + "): scores are level."); continue; }
    if (!stage) { errors.push(label + " (" + p1 + " v " + p2 + "): no stage found above it — choose a default stage."); continue; }

    // A heading we couldn't read sits above this match, so its stage/grade may
    // be wrong — say so next to the stage that was actually used.
    if (ignoredHeadings.length > 0) {
      notes.push("Didn't recognise the heading \"" + ignoredHeadings.join("\", \"") + "\" — the matches under it were read as " +
        (stage || "no stage") + ". Check the list below, or choose the right stage/draw above.");
      ignoredHeadings = [];
    }

    var m = buildMatch(grade || "", stage, group, p1, p2, t.s1, t.s2, a1, a2);
    matches.push(m);
    k += consumed;
  }
  ignoredHeadings.forEach(function(h) { notes.push("Ignored heading \"" + h + "\"."); });

  if (matches.length === 0 && errors.length === 0) errors.push("Couldn't find any matches in that text.");
  return { matches: matches, errors: errors, notes: notes };
}

// Forces every knockout match in a paste into the Main or Consolation draw
// ("Final" <-> "Consolation Final"). Anything else (draw = "auto") is left as read.
export function applyDraw(matches, draw) {
  if (draw !== "Main" && draw !== "Consolation") return { matches: matches, errors: [] };
  var errors = [];
  var out = [];
  matches.forEach(function(m) {
    if (m.stage === "Round Robin") { out.push(m); return; }
    var base = m.stage.replace(/^Consolation\s+/, "");
    var stage = draw === "Consolation" ? "Consolation " + base : base;
    if (STAGES.indexOf(stage) < 0) {
      errors.push(m.player1 + " v " + m.player2 + ": \"" + stage + "\" isn't a stage that exists in the " + draw.toLowerCase() + " draw.");
      return;
    }
    out.push(Object.assign({}, m, { stage: stage }));
  });
  return { matches: out, errors: errors };
}

// Picks the right reader for whatever was pasted.
// defaults: { grade, stage, draw: "auto" | "Main" | "Consolation" }
export function parsePaste(text, defaults) {
  defaults = defaults || {};
  var r;
  if (looksLikeDartConnect(text)) {
    r = parseDartConnect(text, defaults);
    r.format = "DartConnect";
  } else {
    r = parseBulk(text);
    r.notes = [];
    r.format = "table";
  }
  var forced = applyDraw(r.matches, defaults.draw);
  r.matches = forced.matches;
  r.errors = r.errors.concat(forced.errors);
  return r;
}

// One line: grade, stage, player1, player2, score1, score2 [, avg1, avg2]
export function parseBulk(text) {
  var matches = [];
  var errors = [];
  text.split(/\r?\n/).forEach(function(raw, i) {
    var line = raw.trim();
    if (!line) return;
    if (i === 0 && /player/i.test(line)) return; // header row
    var n = "Line " + (i + 1) + ": ";
    var parts = splitLine(line);
    if (parts.error) { errors.push(n + parts.error + "."); return; }

    var grade = normaliseGrade(parts.grade);
    var stage = normaliseStage(parts.stage);
    var p1 = cleanName(parts.p1);
    var p2 = cleanName(parts.p2);
    var s1 = parseInt(parts.nums[0], 10);
    var s2 = parseInt(parts.nums[1], 10);
    if (grade === null) { errors.push(n + "unknown grade \"" + parts.grade + "\"."); return; }
    if (!stage) { errors.push(n + "unknown stage \"" + parts.stage + "\"."); return; }
    if (!p1 || !p2) { errors.push(n + "missing player name."); return; }
    if (p1 === p2) { errors.push(n + "both players have the same name."); return; }
    if (isNaN(s1) || isNaN(s2) || s1 === s2) { errors.push(n + "scores must be two different numbers."); return; }
    matches.push(buildMatch(grade, stage, "", p1, p2, s1, s2, numberOrNull(parts.nums[2]), numberOrNull(parts.nums[3])));
  });
  return { matches: matches, errors: errors };
}
